<style lang="postcss">
	.multiplechoice-input-cover {
		gap: 0.35rem;
	}
	.multiplechoice-input-cover.one-row {
		display: flex;
		flex-direction: column;
		align-items: stretch;
	}
	.multiplechoice-input-cover.multiple-rows {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
	}
	.multiplechoice-input-cover.vertical {
		display: grid;
		align-items: stretch;
	}
	@container (min-width: 20rem) {
		.multiplechoice-input-cover {
			gap: 0.5rem;
		}
		.multiplechoice-input-cover.horizontal {
			display: grid;
			align-items: flex-start;
		}
	}
	@container (max-width: 419px) {
		.multiplechoice-input-cover.over-2.vertical,
		.multiplechoice-input-cover.over-2.horizontal {
			display: grid;
			grid-template-columns: repeat(2, minmax(1em, 1fr)) !important;
		}
	}
	@container (max-width: 319px) {
		.multiplechoice-input-cover.over-2.vertical,
		.multiplechoice-input-cover.vertical {
			display: grid;
			grid-template-columns: repeat(1, minmax(1em, 1fr)) !important;
		}
	}
</style>

<script lang="ts">
	import Label from "$form/Fields/Shared/Label.svelte";
	import { clickOutside } from "$lib/actions";
	import Option from "./Option.svelte";
	import Description from "$form/Fields/Shared/Description.svelte";
	import { createEventDispatcher, onDestroy, onMount } from "svelte";
	import { optionIsInList } from "$lib/helpers/functions";
	import FieldError from "$form/Fields/Shared/FieldError.svelte";
	const dispatch = createEventDispatcher();
	import type { Writable } from "svelte/store";
	import { getContext } from "svelte";
	import { BROWSER } from "esm-env";

	export let field: FieldDisplay & { type: "MultipleChoice" };
	export let value: ChoiceValue[] | undefined = undefined;

	let visit = getContext<Writable<Visit>>("visit");
	let optionsCoverRef: HTMLDivElement;
	let isActive = false;

	const handleChange = (active: boolean, option: ChoiceOption) => {
		const max = field.settings.max === "" ? "99" : field.settings.max;
		if (active) {
			if (!field.settings.multiple || value === undefined) {
				value = [option];
			} else if (max && value.length < parseInt(max)) {
				value = [...value, option];
			}
		} else {
			if (!field.settings.multiple) {
				value = [];
			} else {
				value = value.filter(
					(v) => v.value !== option.value || v.label !== option.label
				);
			}
		}
	};

	const getRandomizedOptions = (options: ChoiceOption[]) => {
		const seed = $visit.session_id ?? "no-seed";
		const seededRandom = (function () {
			let s =
				1779033703 ^
				seed.split("").reduce((a, b) => {
					return ((a << 5) - a + b.charCodeAt(0)) | 0;
				}, 0);
			return function () {
				s = Math.imul(s ^ (s >>> 15), 1 | s);
				s = (s + Math.imul(s ^ (s >>> 7), 61 | s)) ^ s;
				return ((s ^ (s >>> 14)) >>> 0) / 4294967296;
			};
		})();
		const optionsCopy = [...options];
		if (field.settings.randomize) {
			for (let i = optionsCopy.length - 1; i > 0; i--) {
				const j = Math.floor(seededRandom() * (i + 1));
				[optionsCopy[i], optionsCopy[j]] = [optionsCopy[j], optionsCopy[i]];
			}
		}
		return optionsCopy;
	};

	const clickListener = () => {
		if (isActive) return;
		isActive = true;
		dispatch("updateOther", {
			active: true
		});
	};

	onMount(() => {
		if (BROWSER) {
			optionsCoverRef.addEventListener("click", clickListener);
		}
	});

	onDestroy(() => {
		if (BROWSER) {
			optionsCoverRef.removeEventListener("click", clickListener);
		}
	});
</script>

<div class="field-label-cover">
	{#if field.labelHTMLComputed && field.settings.hideLabel !== true}
		<Label html="{field.labelHTMLComputed}" />
		{#if field.descriptionHTMLComputed}
			<Description html="{field.descriptionHTMLComputed}" />
		{/if}
	{/if}
</div>
<div
	bind:this="{optionsCoverRef}"
	use:clickOutside="{{
		callbackFunction: () => {
			if (!isActive) return;
			dispatch('updateOther', {
				active: false
			});
		}
	}}"
	class="field-input-cover">
	<div
		class="multiplechoice-input-cover {parseInt(
			field.styling.optionsInRow,
			10
		) >= 2
			? 'over-2'
			: 'not-over-2'} {field.styling.optionsInRow
			? 'one-row'
			: 'multiple-rows'} {field.styling.vertical === false
			? 'horizontal'
			: 'vertical'}"
		style="{field.styling.optionsInRow
			? `grid-template-columns: repeat(${
					field.styling.optionsInRow || 1
				}, minmax(1em, 1fr))`
			: ''}">
		{#each getRandomizedOptions(field.options.list) as option}
			{@const checked = value ? optionIsInList(option, value) : false}
			<Option
				active="{checked}"
				option="{option}"
				field="{field}"
				opacity="{true}"
				on:updateValue="{(value) => {
					handleChange(value.detail.active, {
						label: option.label,
						value: option.value
					});
				}}" />
		{/each}
	</div>
	<FieldError
		visible="{field.tainted}"
		valid="{field.valid}"
		errorNumber="{field.errorNumber}"
		errorType="{field.errorType}" />
</div>
